/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {useMemo} from 'react';

import Chart from 'react-apexcharts';

import {ApexBarSerie, SearchVolumeData} from '../../util/interfaces';

interface Props {
    searchVolumes: Array<SearchVolumeData>;
    width?: number | string;
    height?: number | string;
}

type DataAnnotation = {x: string; y: number; text: string; fillColor: string};

function getDiffPercentage(current: number, prev: number): [string, boolean] {
    const diffPercentage = ((current - prev) / prev) * 100;
    if (diffPercentage < 0) return [`-${Math.round(Math.abs(diffPercentage))}%`, false];
    return [`+${Math.round(diffPercentage)}%`, true];
}

function getSearchVolumeBarData(data: SearchVolumeData): [string, Array<string>, Array<number>, Array<DataAnnotation>] {
    const labels: Array<string> = [];
    const values: Array<number> = [];
    const annotations: Array<DataAnnotation> = [];
    for (let ix = 0; ix < data.periods.length; ix++) {
        const period = data.periods[ix];
        labels.push(period.period);
        values.push(period.total);
        if (ix > 0) {
            const [diffPercentage, isPositive] = getDiffPercentage(period.total, data.periods[ix - 1].total);
            annotations.push({
                x: period.period,
                y: period.total,
                text: diffPercentage,
                fillColor: isPositive ? '#74c077' : '#f76e66'
            });
        }
    }
    return [data.search_term, labels, values, annotations];
}

export default function SearchVolumeBarChart(props: Props) {
    const {searchVolumes, height} = props;
    const [options, series] = useMemo(() => {
        let xLabels: Array<string> = [];
        const series: Array<ApexBarSerie> = [];
        let pointAnnotations: Array<DataAnnotation> = [];
        for (const searchVolume of searchVolumes) {
            const [seriesName, labels, serieValues, annotations] = getSearchVolumeBarData(searchVolume);
            xLabels = xLabels.concat(labels);
            series.push({name: seriesName, data: serieValues});
            if (searchVolumes.length === 1) pointAnnotations = pointAnnotations.concat(annotations);
        }
        const options: any = {
            chart: {
                toolbar: {show: false},
                legend: {show: searchVolumes.length > 1}
            },
            dataLabels: {enabled: false},
            xaxis: {categories: Array.from(new Set(xLabels)), position: 'top'},
            legend: {
                markers: {
                    customHTML: function () {
                        return (
                            '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="check" class="svg-inline--fa fa-check legend-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">' +
                            '<path fill="currentColor" d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>' +
                            '</svg>'
                        );
                    }
                }
            },
            annotations: {
                points: pointAnnotations.map((annotation: DataAnnotation) => ({
                    x: annotation.x,
                    y: annotation.y,
                    marker: {size: 0, strokeWidth: 0},
                    label: {
                        text: annotation.text,
                        style: {
                            background: annotation.fillColor,
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: {
                                left: 5,
                                right: 5,
                                top: 0,
                                bottom: 2
                            }
                        },
                        borderWidth: 0
                    }
                }))
            }
        };
        return [options, series];
    }, [searchVolumes]);
    const chartKey = useMemo(
        () => searchVolumes.map(searchVolume => searchVolume.search_term).join('|'),
        [searchVolumes]
    );

    return (
        <div className='search-volume-bar-chart'>
            <Chart key={chartKey} options={options} series={series} type='bar' height={height} />
        </div>
    );
}
